import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { EventBus } from '../core/EventBus';

export interface ProjectileData {
  id: number;
  active: boolean;
  mesh: THREE.Mesh;
  body: CANNON.Body;
  damage: number;
  speed: number;
  direction: THREE.Vector3;
  lifetime: number;
  spawnTime: number;
  color: THREE.Color;
}

export class ProjectilePool {
  private pool: ProjectileData[] = [];
  private scene: THREE.Scene;
  private physicsWorld: PhysicsWorld;
  private nextId = 0;

  private readonly POOL_SIZE = 100;
  private readonly DEFAULT_LIFETIME = 3; // seconds
  private readonly PROJECTILE_RADIUS = 0.1;

  constructor(scene: THREE.Scene, physicsWorld: PhysicsWorld) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    this.initializePool();
  }

  private initializePool(): void {
    for (let i = 0; i < this.POOL_SIZE; i++) {
      const projectile = this.createProjectile();
      projectile.active = false;
      projectile.mesh.visible = false;
      this.pool.push(projectile);
    }
  }

  private createProjectile(): ProjectileData {
    // Create mesh
    const geometry = new THREE.SphereGeometry(this.PROJECTILE_RADIUS, 8, 8);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.9,
    });
    const mesh = new THREE.Mesh(geometry, material);
    this.scene.add(mesh);

    // Add glow effect
    const glowGeometry = new THREE.SphereGeometry(this.PROJECTILE_RADIUS * 2, 8, 8);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.3,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    mesh.add(glow);

    // Create physics body
    const shape = new CANNON.Sphere(this.PROJECTILE_RADIUS);
    const body = new CANNON.Body({
      mass: 0.1,
      shape: shape,
      collisionResponse: false, // Projectiles are sensors
    });
    body.collisionFilterGroup = 4; // Projectile group
    body.collisionFilterMask = 2 | 8; // Collide with enemies and destructibles

    return {
      id: this.nextId++,
      active: false,
      mesh,
      body,
      damage: 0,
      speed: 0,
      direction: new THREE.Vector3(),
      lifetime: this.DEFAULT_LIFETIME,
      spawnTime: 0,
      color: new THREE.Color(0x00ffff),
    };
  }

  public spawn(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    speed: number,
    damage: number,
    color: THREE.Color,
    lifetime: number = this.DEFAULT_LIFETIME
  ): ProjectileData | null {
    // Find inactive projectile
    const projectile = this.pool.find((p) => !p.active);
    if (!projectile) {
      console.warn('Projectile pool exhausted');
      return null;
    }

    // Activate projectile
    projectile.active = true;
    projectile.damage = damage;
    projectile.speed = speed;
    projectile.direction.copy(direction).normalize();
    projectile.lifetime = lifetime;
    projectile.spawnTime = performance.now() / 1000;
    projectile.color.copy(color);

    // Set position
    projectile.mesh.position.copy(origin);
    projectile.mesh.visible = true;

    // Update color
    const material = projectile.mesh.material as THREE.MeshBasicMaterial;
    material.color.copy(color);
    const glowMesh = projectile.mesh.children[0] as THREE.Mesh;
    if (glowMesh) {
      (glowMesh.material as THREE.MeshBasicMaterial).color.copy(color);
    }

    // Set physics body position
    projectile.body.position.set(origin.x, origin.y, origin.z);
    projectile.body.velocity.set(
      direction.x * speed,
      direction.y * speed,
      direction.z * speed
    );
    this.physicsWorld.addBody(projectile.body);

    return projectile;
  }

  public despawn(projectile: ProjectileData): void {
    projectile.active = false;
    projectile.mesh.visible = false;
    this.physicsWorld.removeBody(projectile.body);
  }

  public update(dt: number): void {
    const currentTime = performance.now() / 1000;

    for (const projectile of this.pool) {
      if (!projectile.active) continue;

      // Check lifetime
      if (currentTime - projectile.spawnTime > projectile.lifetime) {
        this.despawn(projectile);
        continue;
      }

      // Update mesh position from velocity (simple movement without physics step)
      projectile.mesh.position.x += projectile.direction.x * projectile.speed * dt;
      projectile.mesh.position.y += projectile.direction.y * projectile.speed * dt;
      projectile.mesh.position.z += projectile.direction.z * projectile.speed * dt;

      // Sync physics body
      projectile.body.position.set(
        projectile.mesh.position.x,
        projectile.mesh.position.y,
        projectile.mesh.position.z
      );

      // Check for ground collision (y < 0)
      if (projectile.mesh.position.y < 0) {
        EventBus.emit('projectile:hit', {
          position: {
            x: projectile.mesh.position.x,
            y: 0,
            z: projectile.mesh.position.z,
          },
          targetType: 'ground',
        });
        this.despawn(projectile);
      }
    }
  }

  public checkCollisions(targets: THREE.Object3D[]): void {
    for (const projectile of this.pool) {
      if (!projectile.active) continue;

      const projectilePos = projectile.mesh.position;

      for (const target of targets) {
        if (!target.userData.health) continue;

        const distance = projectilePos.distanceTo(target.position);
        const hitRadius = (target.userData.hitRadius as number) || 1;

        if (distance < hitRadius + this.PROJECTILE_RADIUS) {
          // Hit! Check if entity has a takeDamage method
          const entity = target.userData.entity;
          if (entity && typeof entity.takeDamage === 'function') {
            // Use the entity's damage handling (proper health tracking)
            entity.takeDamage(projectile.damage);
          } else {
            // Fallback to direct userData modification
            const targetId = target.userData.id as string;
            const currentHealth = target.userData.health as number;
            const newHealth = Math.max(0, currentHealth - projectile.damage);
            target.userData.health = newHealth;

            EventBus.emit('target:damaged', {
              id: targetId,
              damage: projectile.damage,
              healthRemaining: newHealth,
            });

            if (newHealth <= 0) {
              EventBus.emit('target:destroyed', {
                id: targetId,
                position: {
                  x: target.position.x,
                  y: target.position.y,
                  z: target.position.z,
                },
                points: target.userData.points || 100,
              });
            }
          }

          EventBus.emit('projectile:hit', {
            position: {
              x: projectilePos.x,
              y: projectilePos.y,
              z: projectilePos.z,
            },
            targetType: 'enemy',
          });

          this.despawn(projectile);
          break;
        }
      }
    }
  }

  public getActiveProjectiles(): ProjectileData[] {
    return this.pool.filter((p) => p.active);
  }

  public destroy(): void {
    for (const projectile of this.pool) {
      this.scene.remove(projectile.mesh);
      if (projectile.active) {
        this.physicsWorld.removeBody(projectile.body);
      }
    }
    this.pool = [];
  }
}
